import moment from 'moment';
import { globalStore } from '@/common';
import { DeliveryApi } from '@/delivery/DeliveryApi';
import type { DeliveryItemDTO } from '@/delivery/DeliveryType';
import type { ShowWorkMeeting } from './model';

const weekFinishDate = (offsetWeeks: number): string =>
  moment().add(offsetWeeks, 'weeks').isoWeekday(5).format('YYYY-MM-DD 00:00:00');

const isParticipant = (item: DeliveryItemDTO, userId: string): boolean =>
  item.userList?.some((u) => String(u.userId) === String(userId)) ?? false;

const meetingsFromItems = (items: DeliveryItemDTO[], userId: string): ShowWorkMeeting[] => {
  const seen = new Set<number>();
  const list: ShowWorkMeeting[] = [];
  for (const item of items) {
    if (!isParticipant(item, userId)) {
      continue;
    }
    const meetingId = item.morningMeetingId;
    if (meetingId == null || seen.has(meetingId)) {
      continue;
    }
    seen.add(meetingId);
    const name =
      item.morningMeetingName?.trim() ||
      globalStore.meetingRecord[String(meetingId)]?.name ||
      '';
    if (!name) {
      continue;
    }
    list.push({
      id: String(meetingId),
      name,
      platform: '',
      information: '',
    });
  }
  list.sort((a, b) => {
    const orderA = globalStore.meetingRecord[a.id]?.orderNum ?? 0;
    const orderB = globalStore.meetingRecord[b.id]?.orderNum ?? 0;
    return orderA - orderB;
  });
  return list;
};

/** 与交付页 WorkspaceContext 一致：按当周/下周周五 finishDate 查交付项，取参与项晨会去重 */
export async function resolveWeekMeetings(userId: string | undefined): Promise<{
  thisWeekMeetings: ShowWorkMeeting[];
  nextWeekMeetings: ShowWorkMeeting[];
}> {
  const empty = { thisWeekMeetings: [] as ShowWorkMeeting[], nextWeekMeetings: [] as ShowWorkMeeting[] };
  if (!userId) {
    return empty;
  }
  await globalStore.loadEnumConfigsByType(1);
  const [thisWeekItems, nextWeekItems] = await Promise.all([
    DeliveryApi.getDeliveryList({ finishDate: weekFinishDate(0) }),
    DeliveryApi.getDeliveryList({ finishDate: weekFinishDate(1) }),
  ]);
  const thisWeek = Array.isArray(thisWeekItems) ? thisWeekItems : [];
  const nextWeek = Array.isArray(nextWeekItems) ? nextWeekItems : [];
  return {
    thisWeekMeetings: meetingsFromItems(thisWeek, userId),
    nextWeekMeetings: meetingsFromItems(nextWeek, userId),
  };
}
